// jQuery is a webpack ProvidePlugin global in this legacy bundle, same as every
// other admin Stimulus controller.
import DatatablesController from "../../../datatables_controller";

// KTDatatable for every managed vocabulary index (Tema Webinar, Nama Tipe
// Webinar, Nama Materi Belajar Pajak). One controller serves all three — the
// columns are identical and only the data path differs, supplied per page via
// `data-admin--api--vocabularies--index-data-path`.
//
// Rows are rendered by the datatable, so the per-row edit modals the old
// server-rendered table used are gone. Instead there is ONE edit modal on the
// page, populated from the clicked button's data-* attributes via Bootstrap's
// `show.bs.modal` + `relatedTarget`.
// Every field the shared edit modal owns, declared once.
//
// This used to be three hardcoded `modal.find(...)` lines, and adding
// `shown_on_homepage` to the form silently missed them — so the checkbox never
// reflected the row being edited AND kept its value between rows. Opening a
// flagged group showed it unchecked (saving cleared the flag), and a stale tick
// carried into the next row (saving flagged the wrong group).
//
// The loop below always writes every field, so nothing can survive from the
// previously edited row. Adding a field now means one entry here plus the matching
// `data-*` attribute on the Edit button.
const EDIT_FIELDS = [
  { param: "vocabulary[name]", attribute: "name" },
  { param: "vocabulary[position]", attribute: "position" },
  { param: "vocabulary[is_active]", attribute: "active", checkbox: true },
  {
    param: "vocabulary[shown_on_homepage]",
    attribute: "homepage",
    checkbox: true,
  },
];

export default class extends DatatablesController {
  initialize() {
    super.initialize();
    this.filter = document.querySelector(
      '[data-controller="admin--index-filter"]',
    );
    this.filterTriggerer = this.filter
      ? this.filter.querySelector(
          '[data-admin--index-filter-target="filterTriggerer"]',
        )
      : null;
  }

  connect() {
    super.connect();
    this.bindEditModal();
    this.bindFilter();
  }

  // Pushes the subheader panel's values onto the datatable's data source, matching
  // admin/api/lessons/index_controller.js. Every radio group has an "all" value,
  // which is forwarded verbatim — the server treats anything that is not
  // "true"/"false"/"used"/"unused" as no constraint, so "all" clears the filter
  // rather than needing a separate delete step.
  bindFilter() {
    if (!this.filterTriggerer) return;

    this.filterTriggerer.addEventListener("click", () => {
      window.KTApp.blockPage();

      const params = [];
      const inputs = this.filter.querySelectorAll("input,select");
      for (let i = 0; i < inputs.length; i += 1) {
        const input = inputs[i];
        // The search box lives outside this panel, so only radios matter here.
        if (input.type === "radio" && input.checked) {
          params.push({ name: input.name, value: input.value });
        }
      }

      params.forEach((entry) => {
        this.datatable.setDataSourceParam(entry.name, entry.value);
      });
      // One load() after every param is set — calling it per param (as the older
      // lessons controller does) fires a request per filter.
      if (params.length > 0) this.datatable.load();

      window.KTApp.unblockPage();
      this.filter.click();
    });
  }

  bindEditModal() {
    const modal = $("#editVocabulary");
    if (modal.length === 0) return;

    modal
      .off("show.bs.modal.vocabulary")
      .on("show.bs.modal.vocabulary", (event) => {
        const button = $(event.relatedTarget);
        if (button.length === 0) return;

        // The form posts to a different record each time, so retarget it. The
        // hidden Rails `_method=patch` field is untouched.
        modal.find("form").attr("action", button.data("update-path"));

        EDIT_FIELDS.forEach((field) => {
          // `find` returns an empty set for a field this vocabulary does not have
          // (only Nama Materi Belajar has `shown_on_homepage`), and both jQuery
          // setters below no-op on an empty set — so the webinar vocabularies need
          // no special-casing.
          const input = modal.find(`[name="${field.param}"]`);
          const value = button.data(field.attribute);

          if (field.checkbox) {
            // Written unconditionally: `undefined` becomes unchecked, which is
            // what stops the previous row's value from persisting.
            input.prop("checked", String(value) === "true");
          } else {
            input.val(value === undefined || value === null ? "" : value);
          }
        });
      });
  }

  datatableColumns() {
    return [
      {
        field: "index",
        title: "No",
        sortable: false,
        width: 50,
        type: "number",
        selector: false,
        textAlign: "left",
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          return `<span class="font-weight-bolder">${data.index}</span>`;
        },
      },
      {
        field: "name",
        title: "Nama",
        width: 300,
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          return `<span class="font-weight-bolder text-dark-75">${data.name}</span>`;
        },
      },
      {
        field: "slug",
        title: "Slug",
        width: 250,
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          // Deliberately not `<code class="text-muted">`: that renders pale grey
          // (#B5B5C3) on a pale grey background and was hard to read. The
          // `slug-token` class below keeps the monospace legibility a slug wants
          // but with a properly contrasting colour.
          return `<span class="slug-token">${data.slug}</span>`;
        },
      },
      {
        field: "position",
        title: "Urutan",
        width: 95,
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          return `<span class="font-weight-bolder">${
            data.position === null || data.position === undefined
              ? "-"
              : data.position
          }</span>`;
        },
      },
      {
        field: "usage_count",
        title: "Dipakai",
        sortable: false,
        width: 95,
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          const tone =
            data.usage_count > 0 ? "label-light-primary" : "label-light";
          return `<span class="label label-lg ${tone} label-inline">${data.usage_count}</span>`;
        },
      },
      {
        field: "is_active",
        title: "Status",
        width: 120,
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          const active = data.is_active
            ? '<span class="label label-lg label-light-success label-inline">Aktif</span>'
            : '<span class="label label-lg label-light-danger label-inline">Nonaktif</span>';

          // Second badge rather than a separate column: only Nama Materi Belajar
          // has this flag, and the three vocabularies share this controller, the
          // shared index view and its pinned column widths. `undefined` for the
          // webinar vocabularies, so they render exactly as before.
          if (data.shown_on_homepage === true) {
            return `${active}<span class="label label-lg label-light-primary label-inline mt-1">Homepage</span>`;
          }
          return active;
        },
      },
      {
        field: "Actions",
        title: "Aksi",
        sortable: false,
        width: 100,
        overflow: "visible",
        autoHide: false,
        template: function (data) {
          return `
            <a class="btn btn-sm btn-clean btn-icon" title="Edit"
               data-toggle="modal" data-target="#editVocabulary"
               data-update-path="${data.update_path}"
               data-name="${data.name.replace(/"/g, "&quot;")}"
               data-position="${data.position === null || data.position === undefined ? "" : data.position}"
               data-active="${data.is_active}"
               data-homepage="${data.shown_on_homepage === true}">
              <i class="fa fa-edit text-warning"></i>
            </a>
            <a class="btn btn-sm btn-clean btn-icon" href="${data.delete_path}"
               data-method="delete" data-confirm="Hapus &quot;${data.name.replace(/"/g, "&quot;")}&quot;?"
               title="Hapus">
              <i class="fa fa-trash text-danger"></i>
            </a>
          `;
        },
      },
    ];
  }
}
